/**
 * Enhanced Authentication Service with OTP Support
 * Supports both email/password and phone OTP authentication
 * Includes role-based redirects and secure token management
 */

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

/**
 * Helper: Fetch with credentials (for cookie support)
 */
const authFetch = async (url, options = {}) => {
  try {
    console.log('🌐 API Request:', url, options.method || 'GET');
    
    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Important: Send cookies with requests
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    const data = await response.json();
    console.log('📦 API Response:', response.status, data);
    
    // If response is not ok but has JSON data with message, return it
    if (!response.ok && data.message) {
      return data; // Return error data from backend
    }
    
    return data;
  } catch (error) {
    console.error('🚫 Network Error:', error);
    throw new Error('Unable to connect to server. Please check if backend is running on port 8000.');
  }
};

/* ───────── PHONE OTP AUTHENTICATION ───────── */

/**
 * Check if user exists with given phone number
 * @param {string} phone - Phone number (10 digits or with country code)
 * @returns {Promise} - { success, exists, user? }
 */
export const checkUserExists = async (phone) => {
  return authFetch(`${BASE_URL}/auth/check-user`, {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
};

/**
 * Send OTP to phone number
 * @param {string} phone - Phone number (10 digits or with country code)
 * @returns {Promise} - { success, message, phone, debug? }
 */
export const sendOTP = async (phone) => {
  return authFetch(`${BASE_URL}/auth/send-otp`, {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
};

/**
 * Verify OTP and login/register user
 * @param {string} phone - Phone number
 * @param {string} otp - 6-digit OTP
 * @param {string} name - User name (optional, for new users)
 * @returns {Promise} - { success, token, user, isNewUser }
 */
export const verifyOTP = async (phone, otp, name = null) => {
  const payload = { phone, otp };
  if (name) payload.name = name;
  
  return authFetch(`${BASE_URL}/auth/verify-otp`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

/**
 * Resend OTP to phone number
 * @param {string} phone - Phone number
 * @returns {Promise} - { success, message, phone }
 */
export const resendOTP = async (phone) => {
  return authFetch(`${BASE_URL}/auth/resend-otp`, {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
};

/* ───────── ADMIN AUTHENTICATION ───────── */

/**
 * Admin login with email and password
 * @param {string} email - Admin email
 * @param {string} password - Admin password
 * @returns {Promise} - { success, token, user }
 */
export const adminLogin = async (email, password) => {
  return authFetch(`${BASE_URL}/admin/auth/login`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
};

/**
 * Admin logout
 */
export const adminLogout = async () => {
  return authFetch(`${BASE_URL}/admin/auth/logout`, {
    method: 'POST',
  });
};

/* ───────── TOKEN & USER MANAGEMENT ───────── */

/**
 * Logout user (clears cookie and local storage)
 */
export const logout = async () => {
  try {
    await authFetch(`${BASE_URL}/auth/logout`, {
      method: 'POST',
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Clear local storage
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
  }
};

/**
 * Get user profile from backend
 */
export const getUserProfile = async () => {
  const token = localStorage.getItem('userToken');
  if (!token) return null;
  
  try {
    const res = await fetch(`${BASE_URL}/user/profile`, {
      credentials: 'include',
      headers: { 
        Authorization: `Bearer ${token}` 
      },
    });
    
    if (!res.ok) throw new Error('Not authenticated');
    const json = await res.json();
    return json.user || json;
  } catch (error) {
    console.error('Get profile error:', error);
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('userToken');
};

/**
 * Get user data from local storage
 */
export const getCurrentUser = () => {
  const userData = localStorage.getItem('userData');
  return userData ? JSON.parse(userData) : null;
};

/**
 * Check if current user is admin
 */
export const isAdmin = () => {
  const user = getCurrentUser();
  return user && user.role === 'ADMIN';
};

/**
 * Store authentication data in local storage
 */
export const storeAuthData = (token, user) => {
  localStorage.setItem('userToken', token);
  localStorage.setItem('userData', JSON.stringify(user));
};

/**
 * Clear authentication data from local storage
 */
export const clearAuthData = () => {
  localStorage.removeItem('userToken');
  localStorage.removeItem('userData');
};

/* ───────── ROLE-BASED REDIRECT ───────── */

/**
 * Get redirect path based on user role
 * @param {Object} user - User object with role property
 * @returns {string} - Redirect path
 */
export const getRedirectPath = (user) => {
  if (!user) return '/';
  
  if (user.role === 'ADMIN') {
    return '/admin/dashboard';
  }
  
  return '/';
};

/**
 * Handle successful authentication
 * Stores token, user data, and returns redirect path
 * @param {Object} authResponse - Response from login/verify API
 * @returns {string} - Redirect path
 */
export const handleAuthSuccess = (authResponse) => {
  const { token, user, admin } = authResponse;
  const userData = user || admin;
  
  // Store authentication data
  storeAuthData(token, userData);
  
  // Return redirect path based on role
  return getRedirectPath(userData);
};

/* ───────── AUTHENTICATED REQUESTS ───────── */

/**
 * Make authenticated API request with token
 * @param {string} endpoint - API endpoint (e.g., '/user/profile')
 * @param {Object} options - Fetch options
 * @returns {Promise} - API response
 */
export const authenticatedRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem('userToken');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    if (response.status === 401) {
      // Token expired or invalid - clear auth data
      clearAuthData();
      throw new Error('Session expired. Please login again.');
    }
    throw new Error(`HTTP ${response.status}`);
  }
  
  return response.json();
};

const authService = {
  // OTP Auth
  sendOTP,
  verifyOTP,
  resendOTP,
  
  // Admin Auth
  adminLogin,
  adminLogout,
  
  // Token Management
  logout,
  getUserProfile,
  isAuthenticated,
  getCurrentUser,
  isAdmin,
  storeAuthData,
  clearAuthData,
  
  // Helpers
  getRedirectPath,
  handleAuthSuccess,
  authenticatedRequest,
};

export default authService;
